import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import './CollectionsPage.css';
import { useSEO } from '../lib/seo';
import './ShopPage.css';
const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/* ─────────────────────────────────────────────
   Constants
───────────────────────────────────────────── */
const SORT_OPTIONS = [
  { value: 'popular',    label: 'Most Popular' },
  { value: 'rating',     label: 'Top Rated' },
  { value: 'price-asc',  label: 'Price: Low → High' },
  { value: 'price-desc', label: 'Price: High → Low' },
  { value: 'new',        label: 'New Arrivals' },
];

const CARE_LEVELS  = ['Very Easy', 'Easy', 'Moderate', 'Advanced'];
const LIGHT_NEEDS  = ['Low light', 'Indirect', 'Bright indirect', 'Direct sun'];
const PET_OPTIONS  = ['Yes', 'No', 'Mildly Toxic'];
const PAGE_SIZE    = 20;


/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
function normaliseProduct(p) {
  // Derive the display price from the pricing matrix if it exists.
  // Use the minimum price across all entries so the card shows a "from ₹X" price
  // that matches the first / cheapest option the admin has configured.
  let displayPrice = p.price || 0;
  let displayOriginalPrice = p.originalPrice || null;

  if (Array.isArray(p.pricing) && p.pricing.length > 0) {
    const minEntry = p.pricing.reduce((min, e) => (e.price < min.price ? e : min), p.pricing[0]);
    displayPrice = minEntry.price;
    displayOriginalPrice = minEntry.originalPrice || null;
  }

  return {
    ...p,
    id:            p._id || p.id,
    imageUrl:      (p.images && p.images[0]?.url) || p.imageUrl || p.image || null,
    price:         displayPrice,
    originalPrice: displayOriginalPrice,
    rating:        p.rating || 0,
    reviews:       Array.isArray(p.reviews) ? p.reviews.length : (p.reviews || 0),
    badge:         p.badge || null,
    _careLevel:    p.care?.difficulty,
    _light:        p.care?.light,
    _petFriendly:  p.care?.petFriendly,
    inStock:       p.inStock !== false,
  };
}

/* ─────────────────────────────────────────────
   Empty / Error state
───────────────────────────────────────────── */
function EmptyState({ icon, title, body, action, onAction }) {
  return (
    <div className="col-pg__empty">
      <span className="col-pg__empty-icon">{icon}</span>
      <h3 className="col-pg__empty-title">{title}</h3>
      {body   && <p   className="col-pg__empty-body">{body}</p>}
      {action && <button className="col-pg__empty-btn" onClick={onAction}>{action}</button>}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Skeleton loaders
───────────────────────────────────────────── */
function GridSkeletons({ count = 8 }) {
  return (
    <div className="col-pg__grid">
      {[...Array(count)].map((_, i) => (
        <div key={i} className="col-pg__skel-card" />
      ))}
    </div>
  );
}

function CollectionListSkeletons() {
  return (
    <div className="col-pg__skeletons">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="col-pg__skeleton">
          <div className="col-pg__skel-header" />
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Pagination controls
───────────────────────────────────────────── */
function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;

  // Build a compact page list: 1 … p-1 p p+1 … last
  const pages = [];
  const push = v => pages.push(v);
  push(1);
  if (page > 3) push('…');
  for (let p = Math.max(2, page - 1); p <= Math.min(totalPages - 1, page + 1); p++) push(p);
  if (page < totalPages - 2) push('…');
  if (totalPages > 1) push(totalPages);

  return (
    <nav className="col-pg__pagination" aria-label="Product pages">
      <button
        className="col-pg__page-btn col-pg__page-btn--nav"
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        aria-label="Previous page"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>

      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`ellipsis-${i}`} className="col-pg__page-ellipsis">…</span>
        ) : (
          <button
            key={p}
            className={`col-pg__page-btn${p === page ? ' col-pg__page-btn--active' : ''}`}
            onClick={() => onChange(p)}
            aria-current={p === page ? 'page' : undefined}
          >
            {p}
          </button>
        )
      )}

      <button
        className="col-pg__page-btn col-pg__page-btn--nav"
        onClick={() => onChange(page + 1)}
        disabled={page === totalPages}
        aria-label="Next page"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>
    </nav>
  );
}

/* ─────────────────────────────────────────────
   Single collection card — image + name only
   (used on the /collections list view)
───────────────────────────────────────────── */
function CollectionCard({ collection }) {
  return (
    <Link to={`/collections/${collection.slug}`} className="col-card">
      <div className="col-card__img-wrap">
        {collection.image ? (
          <img src={collection.image} alt={collection.name} className="col-card__img" loading="lazy" />
        ) : (
          <div className="col-card__img-placeholder" aria-hidden>🌿</div>
        )}
      </div>
      <h2 className="col-card__name">{collection.name}</h2>
    </Link>
  );
}

/* ─────────────────────────────────────────────
   Single-collection page  (/collections/:slug)
   — styled like ShopPage with hero + sort bar
───────────────────────────────────────────── */
function SingleCollectionView({ slug, addToCart }) {
  const navigate = useNavigate();

  const [collection, setCollection] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [sort,       setSort]       = useState('popular');
  const [maxPrice,   setMaxPrice]   = useState(3000);
  const [careLevels, setCareLevels] = useState([]);
  const [lightNeeds, setLightNeeds] = useState([]);
  const [petFilter,  setPetFilter]  = useState([]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen,   setSortOpen]   = useState(false);
  const [page,       setPage]       = useState(1);
  const sortRef = useRef(null);

  // Close sort dropdown on outside click
  useEffect(() => {
    const h = e => { if (sortRef.current && !sortRef.current.contains(e.target)) setSortOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const fetchProducts = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch(`${BASE}/api/collections/${slug}/products?sort=${sort}`)
      .then(r => { if (!r.ok) throw new Error(`Server error ${r.status}`); return r.json(); })
      .then(data => {
        setCollection(data.collection || null);
        setAllProducts((data.products || []).map(normaliseProduct));
      })
      .catch(err => {
        console.error(err);
        setError('Could not load this collection. Please try again.');
      })
      .finally(() => setLoading(false));
  }, [slug, sort]);

  useEffect(() => {
    const t = setTimeout(fetchProducts, 200);
    return () => clearTimeout(t);
  }, [fetchProducts]);

  // Client-side filtering — price + care level + light + pet + stock
  const products = allProducts.filter(p => {
    if (p.price > maxPrice) return false;
    if (careLevels.length > 0 && !careLevels.includes(p._careLevel)) return false;
    if (lightNeeds.length > 0 && !lightNeeds.includes(p._light)) return false;
    if (petFilter.length  > 0 && !petFilter.includes(p._petFriendly)) return false;
    if (inStockOnly && !p.inStock) return false;
    return true;
  });

  const toggleCare  = level =>
    setCareLevels(prev => prev.includes(level) ? prev.filter(l => l !== level) : [...prev, level]);

  const toggleLight = level =>
    setLightNeeds(prev => prev.includes(level) ? prev.filter(l => l !== level) : [...prev, level]);

  const togglePet = val =>
    setPetFilter(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);

  const hasActiveFilters = maxPrice < 3000 || careLevels.length > 0 || lightNeeds.length > 0 || petFilter.length > 0 || inStockOnly;
  const clearAll = () => { setMaxPrice(3000); setCareLevels([]); setLightNeeds([]); setPetFilter([]); setInStockOnly(false); };

  const sortLabel = SORT_OPTIONS.find(o => o.value === sort)?.label || 'Sort by';

  // Reset to page 1 whenever the collection, sort, or filters change the result set
  useEffect(() => {
    setPage(1);
  }, [slug, sort, maxPrice, careLevels, lightNeeds, petFilter, inStockOnly]);

  const totalPages = Math.max(1, Math.ceil(products.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedProducts = products.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const goToPage = p => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <main className="col-pg col-pg--single">

      {/* ── Hero ── */}
      {(() => {
        if (loading && !collection) {
          return <div className="col-pg__shop-header col-pg__shop-header--skel" aria-hidden />;
        }
        const showText = collection?.showTitleDescription !== false;
        const hasImage = collection?.showHeroImage !== false && (collection?.image || collection?.mobileImage);

        // Nothing to show at all — skip the hero entirely.
        if (!showText && !hasImage) return null;

        return (
          <div className={`col-pg__shop-header${!hasImage ? ' col-pg__shop-header--no-image' : ''}`}>
            {hasImage && (() => {
              const desktopSrc = collection?.image;
              const mobileSrc  = collection?.mobileImage || desktopSrc;
              return (
                <>
                  <img
                    src={mobileSrc}
                    alt={collection?.name || 'Collection'}
                    className="col-pg__shop-hero-img col-pg__shop-hero-img--mobile"
                    loading="eager"
                  />
                  <img
                    src={desktopSrc}
                    alt={collection?.name || 'Collection'}
                    className="col-pg__shop-hero-img col-pg__shop-hero-img--desktop"
                    loading="eager"
                  />
                </>
              );
            })()}
            {showText && (
              <div className="col-pg__shop-hero-overlay">
                <div className="col-pg__shop-hero-text">
                  <h1 className="col-pg__shop-title">{collection?.name || 'Collection'}</h1>
                  {collection?.description && (
                    <p className="col-pg__shop-sub">{collection.description}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Filter / Sort Bar ── */}
      <div className="shop__bar">
        <div className="shop__bar-inner">

          {/* Filter toggle */}
          <button
            className={`shop__bar-filter${filterOpen ? ' shop__bar-filter--open' : ''}`}
            onClick={() => setFilterOpen(o => !o)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <line x1="4" y1="6" x2="20" y2="6"/>
              <line x1="8" y1="12" x2="16" y2="12"/>
              <line x1="11" y1="18" x2="13" y2="18"/>
            </svg>
            Filter
            {hasActiveFilters && <span className="shop__bar-dot" />}
          </button>

          {/* Sort dropdown */}
          <div className="shop__sort-wrap" ref={sortRef}>
            <button className="shop__sort-btn" onClick={() => setSortOpen(o => !o)}>
              {sortLabel}
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                style={{ transform: sortOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }}>
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            {sortOpen && (
              <div className="shop__sort-dropdown">
                {SORT_OPTIONS.map(o => (
                  <button
                    key={o.value}
                    className={`shop__sort-option${sort === o.value ? ' shop__sort-option--active' : ''}`}
                    onClick={() => { setSort(o.value); setSortOpen(false); }}
                  >
                    {o.label}
                    {sort === o.value && (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Expandable filter panel */}
        {filterOpen && (
          <div className="shop__filter-panel">
            <div className="shop__filter-panel-inner">
              <div className="shop__fp-group">
                <h4 className="shop__fp-title">
                  Max Price <span>₹{maxPrice.toLocaleString()}</span>
                </h4>
                <input
                  type="range" min="300" max="3000" step="100"
                  value={maxPrice}
                  onChange={e => setMaxPrice(Number(e.target.value))}
                  className="filter-range"
                />
                <div className="filter-range__labels"><span>₹300</span><span>₹3,000</span></div>
              </div>

              <div className="shop__fp-group">
                <h4 className="shop__fp-title">Care Level</h4>
                <div className="shop__fp-checks">
                  {CARE_LEVELS.map(l => (
                    <label key={l} className="shop__fp-check">
                      <input
                        type="checkbox"
                        checked={careLevels.includes(l)}
                        onChange={() => toggleCare(l)}
                      />
                      <span className="shop__fp-box" />
                      {l}
                    </label>
                  ))}
                </div>
              </div>

              <div className="shop__fp-group">
                <h4 className="shop__fp-title">Light Needs</h4>
                <div className="shop__fp-checks">
                  {LIGHT_NEEDS.map(l => (
                    <label key={l} className="shop__fp-check">
                      <input
                        type="checkbox"
                        checked={lightNeeds.includes(l)}
                        onChange={() => toggleLight(l)}
                      />
                      <span className="shop__fp-box" />
                      {l}
                    </label>
                  ))}
                </div>
              </div>

              <div className="shop__fp-group">
                <h4 className="shop__fp-title">Pet Friendly</h4>
                <div className="shop__fp-checks">
                  {PET_OPTIONS.map(v => (
                    <label key={v} className="shop__fp-check">
                      <input
                        type="checkbox"
                        checked={petFilter.includes(v)}
                        onChange={() => togglePet(v)}
                      />
                      <span className="shop__fp-box" />
                      {v}
                    </label>
                  ))}
                </div>
              </div>

              <div className="shop__fp-group">
                <label className="shop__fp-check">
                  <input
                    type="checkbox"
                    checked={inStockOnly}
                    onChange={e => setInStockOnly(e.target.checked)}
                  />
                  <span className="shop__fp-box" />
                  <strong>In Stock Only</strong>
                </label>
              </div>

              {hasActiveFilters && (
                <button className="shop__fp-clear" onClick={clearAll}>
                  Clear all filters
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Active filter tags ── */}
      {hasActiveFilters && (
        <div className="shop__active-filters">
          <div className="shop__active-filters-inner">
            {maxPrice < 3000 && (
              <span className="shop__filter-tag">
                ₹ ≤ {maxPrice.toLocaleString()}{' '}
                <button onClick={() => setMaxPrice(3000)}>✕</button>
              </span>
            )}
            {careLevels.map(l => (
              <span key={l} className="shop__filter-tag">
                {l} <button onClick={() => toggleCare(l)}>✕</button>
              </span>
            ))}
            {lightNeeds.map(l => (
              <span key={l} className="shop__filter-tag">
                💡 {l} <button onClick={() => toggleLight(l)}>✕</button>
              </span>
            ))}
            {petFilter.map(v => (
              <span key={v} className="shop__filter-tag">
                🐾 {v} <button onClick={() => togglePet(v)}>✕</button>
              </span>
            ))}
            {inStockOnly && (
              <span className="shop__filter-tag">
                In Stock <button onClick={() => setInStockOnly(false)}>✕</button>
              </span>
            )}
            <button className="shop__clear-all" onClick={clearAll}>Clear all</button>
          </div>
        </div>
      )}

      {/* ── Product Grid ── */}
      <div className="col-pg__content">
        {loading ? (
          <GridSkeletons count={8} />
        ) : error ? (
          <EmptyState icon="⚠️" title="Something went wrong" body={error} action="Retry" onAction={fetchProducts} />
        ) : products.length === 0 ? (
          <EmptyState
            icon="🌱"
            title="No plants here yet"
            body="This collection is being curated. Check back soon."
            action="Browse all collections"
            onAction={() => navigate('/collections')}
          />
        ) : (
          <>
            <div className="col-pg__grid">
              {pagedProducts.map(p => (
                <ProductCard key={p.id || p._id} product={p} addToCart={addToCart} />
              ))}
            </div>
            <Pagination page={safePage} totalPages={totalPages} onChange={goToPage} />
          </>
        )}
      </div>

    </main>
  );
}

/* ─────────────────────────────────────────────
   All-collections list view  (/collections)
───────────────────────────────────────────── */
function AllCollectionsView() {
  const navigate = useNavigate();

  const [collections, setCollections] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    fetch(`${BASE}/api/collections`)
      .then(r => { if (!r.ok) throw new Error(`Server error ${r.status}`); return r.json(); })
      .then(data => {
        const list = Array.isArray(data) ? data : (data.collections || []);
        setCollections(list.filter(c => c.active !== false));
      })
      .catch(err => { console.error(err); setError('Could not load collections. Please try again.'); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <main className="col-pg">

      {/* ── Page Hero ── */}
      <div className="col-pg__hero">
        <div className="col-pg__hero-inner">
          <p className="col-pg__hero-eyebrow">Curated for every space &amp; style</p>
          <h1 className="col-pg__hero-title">Our Collections</h1>
          <p className="col-pg__hero-sub">
            Handpicked plant families — browse by vibe, care level, or look.
          </p>
        </div>
        <div className="col-pg__hero-blob col-pg__hero-blob--1" aria-hidden />
        <div className="col-pg__hero-blob col-pg__hero-blob--2" aria-hidden />
      </div>

      {/* ── Breadcrumb ── */}
      <nav className="col-pg__breadcrumb" aria-label="Breadcrumb">
        <button className="col-pg__bc-link" onClick={() => navigate('/')}>Home</button>
        <span className="col-pg__bc-sep" aria-hidden>/</span>
        <span className="col-pg__bc-current">Collections</span>
      </nav>

      {/* ── Content ── */}
      <div className="col-pg__body">
        {loading ? (
          <CollectionListSkeletons />
        ) : error ? (
          <EmptyState icon="⚠️" title="Something went wrong" body={error} action="Retry" onAction={load} />
        ) : collections.length === 0 ? (
          <EmptyState
            icon="🌿"
            title="No collections yet"
            body="Check back soon — we're curating new plant families for you."
            action="Browse all plants"
            onAction={() => navigate('/collections/plants')}
          />
        ) : (
          <div className="col-pg__cards">
            {collections.map((col) => (
              <CollectionCard key={col._id || col.slug} collection={col} />
            ))}
          </div>
        )}
      </div>

    </main>
  );
}

/* ─────────────────────────────────────────────
   Router — picks view based on :slug param
───────────────────────────────────────────── */
export default function CollectionsPage({ addToCart }) {
  const { slug } = useParams();
  const _collectionTitle = slug
    ? slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : 'All Plants';
  useSEO({
    title: `${_collectionTitle} — Shop Indoor Plants`,
    description: `Browse our ${_collectionTitle} collection. Premium indoor plants delivered across India with Naturiq.`,
    url: slug ? `/collections/${slug}` : '/collections',
  });

  return slug
    ? <SingleCollectionView slug={slug} addToCart={addToCart} />
    : <AllCollectionsView   />;
}